import React, { FC, useMemo, useState } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  AnalyticsKeywordsAnalysisResponse,
  AnalyticsKeywordsAnalysisResponseItem,
} from 'src/services/__generated__/api'
import { Card } from 'src/components/Card'
import en from 'src/constants/en'
import { useGetInformationTooltipsQuery } from 'src/reactQuery/useGetInformationTooltips'
import { SectionTitle } from 'src/components/SectionTitle'
import { ScrollBarContainer } from 'src/components/ScrollBarContainer'
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter'
import { HeaderCell } from 'src/components/table/HeaderCell'
import { NameCell } from 'src/components/table/NameCell'
import { BorderedRow } from 'src/components/table/BorderedRow'
import { SentimentByMostRated } from 'src/components/table/SentimentByMostRated'
import { MentionCell } from 'src/components/table/MentionCell'
import { numberWithCommas } from 'src/utils/numberFormat'
import { StickyHead } from 'src/components/table/StickyHead'
import CardFilterButton from 'src/components/CardFilterButton'
import { BaseModal } from 'src/containers/BaseModal'
import { Skeleton } from 'src/components/Skeleton'
import { UseGetKeywordsAnalysisParams } from 'src/reactQuery/useGetKeywordsAnalysis/interfaces/useGetKeywordsAnalysis.interface'
import styles from './keywordAnalyses.module.scss'
import { FilterModalContent } from './FilterModalContent'

// Real dimensions: a body cell is 20px/20px padding (borderedRow.module.scss)
// around 16px/500 text, so ~59px per row. .tableContent caps at 544px
// (keywordAnalyses.module.scss), which fits 8 rows plus the 34px sticky header
// without overflowing — so the skeleton reserves the table's real footprint.
const SKELETON_ROW_COUNT = 8
const SKELETON_CELL_HEIGHT = 20
// One width per column, in the column order defined below (keyword,
// mostlyRated, positives, neutrals, negatives, total).
const SKELETON_CELL_WIDTHS = [120, 76, 96, 96, 96, 64]

interface KeywordAnalysesProps {
  applyFilters?: (params: {
    amount?: string
    selectedKeywords?: string[]
    type?: string
  }) => void
  isLoading?: boolean
  keywordAnalysesFilters?: UseGetKeywordsAnalysisParams
  keywordsAnalysisData?: AnalyticsKeywordsAnalysisResponse
}

export const KeywordAnalyses: FC<KeywordAnalysesProps> = ({
  keywordsAnalysisData,
  isLoading,
  keywordAnalysesFilters,
  applyFilters,
}) => {
  const allKeywords = keywordsAnalysisData?.allKeywords
  const selectedKeywords = keywordsAnalysisData?.selectedKeywords

  // Skeletons are for the FIRST load only — when there is genuinely nothing on
  // screen. A refetch (a new filter or date range) keeps the existing rows up
  // and lets the SectionTitle spinner report the work instead. Previously the
  // loading and empty states shared a branch and the widget rendered a single
  // line of "Loading..." text, collapsing the 544px table to ~20px and jumping
  // open when the rows landed.
  const isInitialLoading = !!isLoading && !keywordsAnalysisData

  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false)

  const { data: informationTooltips } = useGetInformationTooltipsQuery()

  const columnHelper =
    createColumnHelper<AnalyticsKeywordsAnalysisResponseItem>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('keyword', {
        cell: (info) => (
          <NameCell>{capitalizeFirstLetter(info.getValue() || '')}</NameCell>
        ),
        header: () => <HeaderCell>{en.keyword}</HeaderCell>,
        id: 'name',
      }),
      columnHelper.accessor('mostlyRated', {
        cell: (info) => (
          <SentimentByMostRated sentiment={info.getValue() || ''} />
        ),
        header: () => <HeaderCell>{en.mostlyRatedAs}</HeaderCell>,
        id: 'mostlyRated',
      }),
      columnHelper.accessor('positives', {
        cell: (info) => {
          const { row } = info

          return (
            <MentionCell
              percentage={row.original.positivePercentage}
              value={info.getValue()}
            />
          )
        },
        header: () => <HeaderCell>{en.positiveMentions}</HeaderCell>,
        id: 'positiveMentions',
      }),
      columnHelper.accessor('neutrals', {
        cell: (info) => {
          const { row } = info

          return (
            <MentionCell
              percentage={row.original.neutralPercentage}
              value={info.getValue()}
            />
          )
        },
        header: () => <HeaderCell>{en.neutralMentions}</HeaderCell>,
        id: 'neutralMentions',
      }),
      columnHelper.accessor('negatives', {
        cell: (info) => {
          const { row } = info

          return (
            <MentionCell
              percentage={row.original.negativePercentage}
              value={info.getValue()}
            />
          )
        },
        header: () => <HeaderCell>{en.negativeMentions}</HeaderCell>,
        id: 'negativeMentions',
      }),
      columnHelper.accessor('total', {
        cell: (info) => (
          <NameCell>{numberWithCommas(info.getValue() || 0)}</NameCell>
        ),
        header: () => <HeaderCell>{en.totalMentions}</HeaderCell>,
        id: 'totalMentions',
      }),
    ],
    []
  )

  const { getHeaderGroups, getRowModel } = useReactTable({
    columns,
    data: allKeywords || [],
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Card className={styles.keywordAnalyses}>
      <SectionTitle
        endItem={
          <CardFilterButton
            className={styles.filterButton}
            onClick={() => setIsFilterModalOpen(true)}
          />
        }
        // On first load the skeleton rows already say "loading"; the spinner
        // would sit on top of them. Keep it for refetches, where real rows stay.
        isLoading={isLoading && !isInitialLoading}
        title={en.keywordAnalyses}
        tooltipContent={informationTooltips?.keywordAnalyses}
      />

      <div className={styles.tableContent}>
        {isInitialLoading ? (
          <ScrollBarContainer className={styles.tableContainer}>
            <table width="100%">
              <StickyHead>
                {getHeaderGroups().map((headerGroup) => (
                  <BorderedRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th align="left" key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </BorderedRow>
                ))}
              </StickyHead>

              <tbody>
                {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <BorderedRow key={rowIdx}>
                    {SKELETON_CELL_WIDTHS.map((width, cellIdx) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td align="left" key={cellIdx}>
                        <Skeleton
                          height={SKELETON_CELL_HEIGHT}
                          radius={4}
                          width={width}
                        />
                      </td>
                    ))}
                  </BorderedRow>
                ))}
              </tbody>
            </table>
          </ScrollBarContainer>
        ) : allKeywords?.length ? (
          <ScrollBarContainer className={styles.tableContainer}>
            <table width="100%">
              <StickyHead>
                {getHeaderGroups().map((headerGroup) => (
                  <BorderedRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th align="left" key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </BorderedRow>
                ))}
              </StickyHead>

              <tbody>
                {getRowModel().rows.map((row) => {
                  return (
                    <BorderedRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td align="left" key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </BorderedRow>
                  )
                })}
              </tbody>
            </table>
          </ScrollBarContainer>
        ) : (
          <div>
            <div>{en.notEnoughDataToDisplay}</div>
          </div>
        )}
      </div>

      <BaseModal isOpen={!!isFilterModalOpen}>
        <FilterModalContent
          allKeywords={allKeywords}
          keywordAnalysesFilters={keywordAnalysesFilters}
          onClose={() => setIsFilterModalOpen(false)}
          onSave={(params) => {
            applyFilters?.(params)

            setIsFilterModalOpen(false)
          }}
          selectedKeywords={selectedKeywords}
        />
      </BaseModal>
    </Card>
  )
}
